import type { FC } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { SLOT } from '@core/components/testIds'
import { logError } from '@core/logging'
import { ensureIntersectionObserver } from '@core/polyfills'

interface Props {
  name: string
  impressionFn: () => void

  testSlottedElement?: Element // TODO(OTO-1193): Remove this prop after adding integration test
}

/**
 * Slot component used for Agent Status widget
 */
export const Slot: FC<Props> = (props) => {
  const { name, impressionFn } = props
  const [isVisible, setVisible] = useState<boolean>(false)
  const observer = useRef<IntersectionObserver | null>(null)
  const isTrackedRef = useRef(false)

  const handleSlotRef = useCallback(
    async (slot: HTMLSlotElement | null) => {
      try {
        if (!observer.current) {
          const IO = await ensureIntersectionObserver()
          observer.current = new IO(
            (intersections) => {
              setVisible(intersections[0]?.intersectionRatio > 0.7)
            },
            {
              threshold: 0.7,
            },
          )
        }
        const slottedElement =
          slot?.assignedNodes()?.[0] || props.testSlottedElement
        if (slottedElement) {
          observer.current?.observe(slottedElement as Element)
        }
      } catch (e) {
        logError(e)
      }
    },
    [props.testSlottedElement],
  ) as unknown as React.Ref<HTMLSlotElement> | undefined

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (isVisible && !isTrackedRef.current) {
        isTrackedRef.current = true
        observer.current?.disconnect()
        impressionFn()
      }
    }, 1000)

    return () => clearTimeout(timeout)
  }, [isVisible, impressionFn])

  useEffect(() => () => {
    observer.current?.disconnect()
  })

  return <slot name={name} ref={handleSlotRef} data-testid={SLOT} />
}
